(() => {
    'use strict';
    /*
    |--------------------------------------------------------------------------
    | Bundle data cache
    |--------------------------------------------------------------------------
    */
    let bundleDataPromise = null;
    let bundleData = null;
    let bundleDataKey = null;
    /*
    |--------------------------------------------------------------------------
    | Helpers
    |--------------------------------------------------------------------------
    */
    function getBundleModal() {
        return document.getElementById('quantity-modal');
    }

    function getActiveBundleCard() {
        return document.querySelector('.quantity-wrapper .option-card.active');
    }

    function getBundleCollectionHandle() {
        const activeCard = getActiveBundleCard();
        return activeCard?.dataset.bundleCollection || '';
    }

    function getCurrentProductHandle() {
        const modal = getBundleModal();
        return modal?.dataset.productHandle || '';
    }
    /*
    |--------------------------------------------------------------------------
    | Selected quantity
    |--------------------------------------------------------------------------
    */
    window.getSelectedBundleQty = function getSelectedBundleQty() {
        const activeCard = getActiveBundleCard();
        const qtyEl = activeCard?.querySelector('.option-qty');
        const qty = parseInt(qtyEl?.textContent?.trim() || '0', 10);
        return Number.isFinite(qty) && qty > 0 ? qty : 0;
    };
    /*
    |--------------------------------------------------------------------------
    | Get selected size
    |--------------------------------------------------------------------------
    */
    window.getSelectedSize = function getSelectedSize() {
      const sizeFieldset = document.querySelector('[data-option-size]');
      if (!sizeFieldset) { return '';}

      const checked = sizeFieldset.querySelector('input[type="radio"]:checked');

      return (checked?.dataset.optionValue || '').trim();
    };
    /*
    |--------------------------------------------------------------------------
    | Fetch collection products
    |--------------------------------------------------------------------------
    */
    async function getCollectionProducts(collectionHandle) {
        if (!collectionHandle) {
            throw new Error('Bundle collection handle is missing');
        }
        const response = await fetch(`/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250`, {
            headers: {
                Accept: 'application/json'
            }
        });
        if (!response.ok) {
            throw new Error(`Unable to load bundle collection: ${collectionHandle}`);
        }
        return response.json();
    }
    /*
    |--------------------------------------------------------------------------
    | Fetch lightweight product JSON
    |--------------------------------------------------------------------------
    */
    async function getBundleProduct(handle) {
        if (!handle) {
            return null;
        }
        const response = await fetch(`/products/${encodeURIComponent(handle)}?view=bundle-json`, {
            headers: {
                Accept: 'application/json'
            }
        });
        if (!response.ok) {
            throw new Error(`Unable to load bundle product: ${handle}`);
        }
        const data = await response.json();
        const product = data?.product;
        if (!product) {
            console.warn('[Bundle] Invalid product response:', handle, data);
            return null;
        }
        /*
        |--------------------------------------------------------------------------
        | Colour
        |--------------------------------------------------------------------------
        |
        | Sloucher Short - Sky High Blue
        |                    ↑
        |
        */
        const colour = product.title?.split(' - ').pop()?.trim() || '';
        /*
        |--------------------------------------------------------------------------
        | Find Size option
        |--------------------------------------------------------------------------
        |
        | Current store returns:
        |
        | options: [
        |   {
        |     name: "Sizes",
        |     position: 1
        |   }
        | ]
        |
        | So find "size" OR "sizes".
        |
        */
        const sizeOptionIndex = (product.options || []).findIndex(option => {
            const name = (option.name || '').trim().toLowerCase();
            return (name === 'size' || name === 'sizes');
        });
        /*
        |--------------------------------------------------------------------------
        | Build normalized variants
        |--------------------------------------------------------------------------
        */
        const variants = (product.variants || []).map(variant => {
            let size = '';
            if (sizeOptionIndex === 0) {
                size = variant.option1 || variant.title || '';
            } else if (sizeOptionIndex === 1) {
                size = variant.option2 || '';
            } else if (sizeOptionIndex === 2) {
                size = variant.option3 || '';
            }
            return {
                id: Number(variant.id),
                size: String(size).trim(),
                /*
                | /products/.../view=bundle-json
                | response does not currently contain
                | variant availability.
                |
                | We'll handle this separately below.
                */
                available: variant.available !== false
            };
        });
        /*
        |--------------------------------------------------------------------------
        | Normalize image
        |--------------------------------------------------------------------------
        */
        const image = product.image?.src || product.images?.[0]?.src || '';
        return {
            handle: product.handle,
            title: product.title,
            colour,
            image,
            variants
        };
    }
    /*
    |--------------------------------------------------------------------------
    | Find matching colour products
    |--------------------------------------------------------------------------
    |
    | Example:
    |
    | Sloucher Short - Sky High Blue
    | Sloucher Short - Worship Worn Blue
    | Sloucher Short - Black
    |
    | Base:
    |
    | Sloucher Short
    |
    */
    function getProductBaseTitle(title) {
        return (title || '').split(' - ')[0].trim().toLowerCase();
    }
    /*
    |--------------------------------------------------------------------------
    | Load bundle data
    |--------------------------------------------------------------------------
    */
    async function loadBundleData(collectionHandle, currentHandle) {
        console.log('[Bundle] Loading collection:', collectionHandle);
        const collection = await getCollectionProducts(collectionHandle);
        const collectionProducts = collection?.products || [];
        if (!collectionProducts.length) {
            console.warn('[Bundle] Collection has no products');
            return [];
        }
        /*
        |--------------------------------------------------------------------------
        | Find current product
        |--------------------------------------------------------------------------
        */
        const currentProduct = collectionProducts.find(product => product.handle === currentHandle);
        if (!currentProduct) {
            console.warn('[Bundle] Current product not found in collection:', currentHandle);
            return [];
        }
        const currentBase = getProductBaseTitle(currentProduct.title);
        console.log('[Bundle] Current base title:', currentBase);
        /*
        |--------------------------------------------------------------------------
        | Find matching colour products
        |--------------------------------------------------------------------------
        */
        const matchingProducts = collectionProducts.filter(product => {
            const base = getProductBaseTitle(product.title);
            return base === currentBase;
        });
        const matchingHandles = matchingProducts.map(product => product.handle);
        console.log('[Bundle] Matching products:', matchingHandles);
        if (!matchingHandles.length) {
            return [];
        }
        /*
        |--------------------------------------------------------------------------
        | Fetch product data in parallel
        |--------------------------------------------------------------------------
        */
        const products = await Promise.all(matchingHandles.map(handle => getBundleProduct(handle)));
        return products.filter(Boolean);
    }
    /*
    |--------------------------------------------------------------------------
    | Preload / cache bundle data
    |--------------------------------------------------------------------------
    */
    function preloadBundleData() {
        const collectionHandle = getBundleCollectionHandle();
        const currentHandle = getCurrentProductHandle();
        if (!collectionHandle || !currentHandle) {
            console.warn('[Bundle] Missing configuration:', {
                collectionHandle,
                currentHandle
            });
            return Promise.resolve([]);
        }
        const newKey = `${collectionHandle}::${currentHandle}`;
        /*
        |--------------------------------------------------------------------------
        | Already loaded for this product/collection
        |--------------------------------------------------------------------------
        */
        if (bundleDataKey === newKey && bundleDataPromise) {
            return bundleDataPromise;
        }
        bundleDataKey = newKey;
        bundleDataPromise = loadBundleData(collectionHandle, currentHandle).then(data => {
            bundleData = data;
            console.log('[Bundle] Product data loaded:', bundleData);
            return bundleData;
        }).catch(error => {
            console.error('[Bundle] Failed to load data:', error);
            /*
            | Allow retry if request failed
            */
            bundleDataPromise = null;
            bundleData = null;
            bundleDataKey = null;
            throw error;
        });
        return bundleDataPromise;
    }
    /*
    |--------------------------------------------------------------------------
    | Create swatch
    |--------------------------------------------------------------------------
    */
    function createBundleSwatch(product) {
        const wrap = document.createElement('div');
        wrap.className = 'bundle-swatch-wrap';
        wrap.dataset.productHandle = product.handle || '';
        /*
        |--------------------------------------------------------------------------
        | Variant map
        |--------------------------------------------------------------------------
        |
        | Expected product JSON:
        |
        | variants: [
        |   {
        |     id: 123,
        |     size: "M",
        |     available: true
        |   }
        | ]
        |
        */
        const variantMap = {};
        (product.variants || []).forEach(variant => {
            const size = (variant.size || '').trim();
            if (!size) {
                return;
            }
            variantMap[size] = {
                id: Number(variant.id),
                available: variant.available === true
            };
        });
        wrap.dataset.variantMap = JSON.stringify(variantMap);
        /*
        |--------------------------------------------------------------------------
        | Swatch button
        |--------------------------------------------------------------------------
        */
        const swatch = document.createElement('button');
        swatch.type = 'button';
        swatch.className = 'bundle-swatch';
        swatch.dataset.value = product.colour || '';
        swatch.dataset.productHandle = product.handle || '';
        /*
        |--------------------------------------------------------------------------
        | Colour
        |--------------------------------------------------------------------------
        */
        const colour = product.colour || product.title?.split(' - ').pop()?.trim() || '';
        swatch.dataset.value = colour;
        swatch.setAttribute('aria-label', colour);
        swatch.setAttribute('aria-pressed', 'false');
        /*
        |--------------------------------------------------------------------------
        | Image
        |--------------------------------------------------------------------------
        */
        if (product.image) {
            const image = document.createElement('img');
            image.src = product.image;
            image.alt = colour;
            image.loading = 'lazy';
            image.width = 80;
            image.height = 80;
            swatch.appendChild(image);
        }
        /*
        |--------------------------------------------------------------------------
        | Select current product by default
        |--------------------------------------------------------------------------
        */
        const currentHandle = getCurrentProductHandle();
        wrap.appendChild(swatch);
        return wrap;
    }
    /*
    |--------------------------------------------------------------------------
    | Build bundle rows
    |--------------------------------------------------------------------------
    */
    window.buildBundleRows = function buildBundleRows() {
        const modal = getBundleModal();
        if (!modal) {
            console.warn('[Bundle] Modal not found');
            return;
        }
        const rowsWrap = modal.querySelector('.bundle-rows');
        if (!rowsWrap) {
            console.warn('[Bundle] .bundle-rows not found');
            return;
        }
        const qty = window.getSelectedBundleQty();
        rowsWrap.innerHTML = '';
        if (!qty) {
            window.validateBundleSelection?.();
            return;
        }
        if (!Array.isArray(bundleData) || !bundleData.length) {
            console.warn('[Bundle] No bundle data available');
            window.validateBundleSelection?.();
            return;
        }
        /*
        |--------------------------------------------------------------------------
        | Build one row for every item
        |--------------------------------------------------------------------------
        */
        for (let i = 1; i <= qty; i++) {
            const row = document.createElement('div');
            row.className = 'bundle-row';
            row.dataset.rowIndex = String(i);
            /*
            |--------------------------------------------------------------------------
            | Row label
            |--------------------------------------------------------------------------
            */
            const label = document.createElement('div');
            label.className = 'bundle-row__label h6';
            label.textContent = `Denim ${i}`;
            /*
            |--------------------------------------------------------------------------
            | Colour wrapper
            |--------------------------------------------------------------------------
            */
            const colours = document.createElement('div');
            colours.className = 'bundle-row__colours h-stack gap-1 wrap';
            /*
            |--------------------------------------------------------------------------
            | Add all matching colours
            |--------------------------------------------------------------------------
            */
            bundleData.forEach(product => {
                const swatch = createBundleSwatch(product);
                colours.appendChild(swatch);
            });
            row.appendChild(label);
            row.appendChild(colours);
            rowsWrap.appendChild(row);
        }
        /*
        |--------------------------------------------------------------------------
        | Apply size filtering
        |--------------------------------------------------------------------------
        */
        window.filterBundleSwatchesBySize?.();
        /*
        |--------------------------------------------------------------------------
        | Validate
        |--------------------------------------------------------------------------
        */
        window.validateBundleSelection?.();
    };
    /*
    |--------------------------------------------------------------------------
    | Validate selections
    |--------------------------------------------------------------------------
    */
    window.validateBundleSelection = function validateBundleSelection() {
        const modal = getBundleModal();
        if (!modal) {
            return false;
        }
        const confirmBtn = modal.querySelector('.bundle-confirm');
        const rows = modal.querySelectorAll('.bundle-row');
        if (!confirmBtn) {
            return false;
        }
        if (!rows.length) {
            confirmBtn.disabled = true;
            confirmBtn.setAttribute('aria-disabled', 'true');
            return false;
        }
        let allSelected = true;
        const selections = [];
        rows.forEach(
            (row, index) => {
                const selected = row.querySelector('.bundle-swatch.is-selected');
                if (!selected) {
                    allSelected = false;
                }
                selections.push({
                    row: index + 1,
                    value: selected?.dataset.value || '',
                    variantId: selected?.dataset.variantId || ''
                });
            });
        confirmBtn.disabled = !allSelected;
        confirmBtn.setAttribute('aria-disabled', String(!allSelected));
        modal.dataset.bundleSelections = JSON.stringify(selections);
        return allSelected;
    };
    /*
    |--------------------------------------------------------------------------
    | Filter swatches by size
    |--------------------------------------------------------------------------
    */
    window.filterBundleSwatchesBySize = function filterBundleSwatchesBySize() {
        const modal = getBundleModal();
        if (!modal) {
            return;
        }
        const size = window.getSelectedSize();
        const wraps = modal.querySelectorAll('.bundle-swatch-wrap');
        /*
         * First:
         * Show/hide colours and attach the correct variant ID.
         */
        wraps.forEach(wrap => {
            let map = {};
            try {
                map = JSON.parse(wrap.dataset.variantMap || '{}');
            } catch (error) {
                console.error('[Bundle] Invalid variant map:', error);
            }
            const swatch = wrap.querySelector('.bundle-swatch');
            if (!swatch) {
                return;
            }
            /*
             * No size selected:
             * Show all colours but don't assign a variant.
             */
            if (!size) {
                wrap.style.display = '';
                delete swatch.dataset.variantId;
                swatch.classList.remove('is-selected');
                swatch.setAttribute('aria-pressed', 'false');
                return;
            }
            const entry = map[size];
            /*
             * Size unavailable for this colour.
             */
            if (!entry || entry.available !== true) {
                wrap.style.display = 'none';
                swatch.classList.remove('is-selected');
                swatch.setAttribute('aria-pressed', 'false');
                delete swatch.dataset.variantId;
                return;
            }
            /*
             * Size available.
             */
            wrap.style.display = '';
            swatch.dataset.variantId = String(entry.id);
            /*
             * Clear existing selection.
             * We'll select the first available colour below.
             */
            swatch.classList.remove('is-selected');
            swatch.setAttribute('aria-pressed', 'false');
        });
        /*
         * Second:
         * For EACH bundle row, select the first available colour.
         */
        const rows = modal.querySelectorAll('.bundle-row');
        rows.forEach(row => {
            const firstAvailableWrap = Array.from(row.querySelectorAll('.bundle-swatch-wrap')).find(wrap => {
                const swatch = wrap.querySelector('.bundle-swatch');
                return (wrap.style.display !== 'none' && swatch && swatch.dataset.variantId);
            });
            if (!firstAvailableWrap) {
                return;
            }
            const firstSwatch = firstAvailableWrap.querySelector('.bundle-swatch');
            firstSwatch.classList.add('is-selected');
            firstSwatch.setAttribute('aria-pressed', 'true');
        });
        /*
         * Re-check Add to Cart button.
         */
        window.validateBundleSelection?.();
    };
    /*
    |--------------------------------------------------------------------------
    | Option card click
    |--------------------------------------------------------------------------
    */
    function setupBundleOptionCards() {
        const optionCards = document.querySelectorAll('.quantity-wrapper .option-card');
        if (!optionCards.length) {
            return;
        }
        optionCards.forEach(card => {
            card.addEventListener('click', async () => {
                optionCards.forEach(option => option.classList.remove('active'));
                card.classList.add('active');
                const qty = window.getSelectedBundleQty();
                /*
                |--------------------------------------------------------------------------
                | Single product
                |--------------------------------------------------------------------------
                */
                if (qty <= 1) {
                    return;
                }
                const modal = getBundleModal();
                if (!modal) {
                    return;
                }
                /*
                |--------------------------------------------------------------------------
                | Clear previous error
                |--------------------------------------------------------------------------
                */
                const errorEl = modal.querySelector('.bundle-error');
                if (errorEl) {
                    errorEl.textContent = '';
                    errorEl.style.display = 'none';
                }
                /*
                |--------------------------------------------------------------------------
                | Load bundle products
                |--------------------------------------------------------------------------
                */
                try {
                    await preloadBundleData();
                    /*
                    |--------------------------------------------------------------------------
                    | Build rows
                    |--------------------------------------------------------------------------
                    */
                    window.buildBundleRows();
                    /*
                    |--------------------------------------------------------------------------
                    | Open modal
                    |--------------------------------------------------------------------------
                    */
                    requestAnimationFrame(
                        () => {
                            if (typeof modal.show === 'function') {
                                modal.show();
                            }
                            window.filterBundleSwatchesBySize?.();
                        });
                } catch (error) {
                    console.error('[Bundle] Could not open bundle:', error);
                    if (errorEl) {
                        errorEl.textContent = 'Unable to load bundle options. Please try again.';
                        errorEl.style.display = 'block';
                    }
                }
            });
        });
    }
    /*
    |--------------------------------------------------------------------------
    | Swatch selection
    |--------------------------------------------------------------------------
    */
    function setupSwatchSelection() {
        document.addEventListener('click', event => {
            const swatch = event.target.closest('.bundle-swatch');
            if (!swatch) {
                return;
            }
            const row = swatch.closest('.bundle-row');
            if (!row) {
                return;
            }
            /*
            |--------------------------------------------------------------------------
            | Deselect other colours in this row
            |--------------------------------------------------------------------------
            */
            row.querySelectorAll('.bundle-swatch').forEach(other => {
                other.classList.remove('is-selected');
                other.setAttribute('aria-pressed', 'false');
            });
            /*
            |--------------------------------------------------------------------------
            | Select clicked colour
            |--------------------------------------------------------------------------
            */
            swatch.classList.add('is-selected');
            swatch.setAttribute('aria-pressed', 'true');
            window.validateBundleSelection?.();
        });
    }
    /*
    |--------------------------------------------------------------------------
    | Size change
    |--------------------------------------------------------------------------
    */
    function setupSizeChange() {
        document.addEventListener('change', event => {
            const input = event.target.closest('input[type="radio"][name^="option"]');
            if (!input) {
                return;
            }
            const fieldset = input.closest('fieldset.variant-picker__option');
            if (!fieldset) {
                return;
            }
            const legend = (fieldset.querySelector('legend')?.textContent || '').toLowerCase();
            if (!legend.includes('size')) {
                return;
            }
            /*
            | Wait for Shopify/theme variant
            | selection to finish.
            */
            requestAnimationFrame(
                () => {
                    window.filterBundleSwatchesBySize?.();
                });
        });
    }
    /*
    |--------------------------------------------------------------------------
    | Add to cart
    |--------------------------------------------------------------------------
    */
    const setButtonLoading = (btn, isLoading) => {
        if (!btn) {
            return;
        }
        btn.disabled = isLoading;
        btn.setAttribute('aria-busy', String(isLoading));
        const text = btn.querySelector('.btn-text');
        if (text) {
            text.textContent = isLoading ? '' : 'Add to cart';
        }
    };
    async function addBundleToCart(btn) {
        const modal = getBundleModal();
        if (!modal) {
            return;
        }
        /*
        |--------------------------------------------------------------------------
        | Validate
        |--------------------------------------------------------------------------
        */
        if (!window.validateBundleSelection()) {
            return;
        }
        setButtonLoading(btn, true);
        try {
            /*
            |--------------------------------------------------------------------------
            | Collect selected variants
            |--------------------------------------------------------------------------
            */
            const selectedSwatches = modal.querySelectorAll('.bundle-swatch.is-selected');
            const items = Array.from(selectedSwatches).map(swatch => ({
                id: Number(swatch.dataset.variantId),
                quantity: 1
            })).filter(item => Number.isFinite(item.id) && item.id > 0);
            if (!items.length) {
                throw new Error('No bundle variants selected');
            }
            /*
            |--------------------------------------------------------------------------
            | Theme loading state
            |--------------------------------------------------------------------------
            */
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', {
                bubbles: true
            }));
            /*
            |--------------------------------------------------------------------------
            | Bundle cart sections
            |--------------------------------------------------------------------------
            */
            const sectionsToBundle = [];
            document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
                bubbles: true,
                detail: {
                    sections: sectionsToBundle
                }
            }));
            /*
            |--------------------------------------------------------------------------
            | Add to cart
            |--------------------------------------------------------------------------
            */
            const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    items,
                    sections: sectionsToBundle
                })
            });
            const responseJson = await response.json();
            /*
            |--------------------------------------------------------------------------
            | Handle error
            |--------------------------------------------------------------------------
            */
            if (!response.ok) {
                throw new Error(responseJson.description || responseJson.message || 'Unable to add items to cart');
            }
            /*
            |--------------------------------------------------------------------------
            | Loading complete
            |--------------------------------------------------------------------------
            */
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', {
                bubbles: true
            }));
            /*
            |--------------------------------------------------------------------------
            | Get updated cart
            |--------------------------------------------------------------------------
            */
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cartContent = await cartResponse.json();
            cartContent.sections = responseJson.sections;
            /*
            |--------------------------------------------------------------------------
            | Variant add event
            |--------------------------------------------------------------------------
            */
            document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
                bubbles: true,
                detail: {
                    items: responseJson.items,
                    cart: cartContent
                }
            }));
            /*
            |--------------------------------------------------------------------------
            | Cart change event
            |--------------------------------------------------------------------------
            */
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                bubbles: true,
                detail: {
                    baseEvent: 'variant:add',
                    cart: cartContent
                }
            }));
            /*
            |--------------------------------------------------------------------------
            | Close modal
            |--------------------------------------------------------------------------
            */
            if (typeof modal.hide === 'function') {
                modal.hide();
            }
        } catch (error) {
            console.error('[Bundle] Add to cart failed:', error);
            const errorEl = modal.querySelector('.bundle-error');
            if (errorEl) {
                errorEl.textContent = error.message || 'Unable to add items to cart';
                errorEl.style.display = 'block';
            }
        } finally {
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', {
                bubbles: true
            }));
            setButtonLoading(btn, false);
        }
    }
    /*
    |--------------------------------------------------------------------------
    | Bundle confirm click
    |--------------------------------------------------------------------------
    */
    function setupBundleConfirm() {
        document.addEventListener('click', event => {
            const btn = event.target.closest('.bundle-confirm');
            if (!btn) {
                return;
            }
            addBundleToCart(btn);
        });
    }
    /*
    |--------------------------------------------------------------------------
    | Initialise
    |--------------------------------------------------------------------------
    */
    function initBundle() {
        setupBundleOptionCards();
        setupSwatchSelection();
        setupSizeChange();
        setupBundleConfirm();
    }
    /*
    |--------------------------------------------------------------------------
    | DOM ready
    |--------------------------------------------------------------------------
    */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initBundle);
    } else {
        initBundle();
    }
})();